import {
  combineReducers,
  configureStore,
  Slice,
  Action
} from '@reduxjs/toolkit'
// import * as Sentry from '@sentry/react'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { combineEpics, createEpicMiddleware, Epic } from 'redux-observable'
import { BehaviorSubject } from 'rxjs'
import { mergeMap } from 'rxjs/operators'

export type StatusType = 'success' | 'error' | 'loading' | 'fetching' | 'idle'
type AppEpic<T = any> = Epic<Action<T>, Action<T>, T, T>

const epic$ = new BehaviorSubject<AppEpic>(combineEpics())
const rootEpic: AppEpic = (action$, store$, dependencies) =>
  epic$.pipe(mergeMap((epic) => epic(action$, store$, dependencies)))

const epicMiddleware = createEpicMiddleware()
// const sentryReduxEnhancer = Sentry.createReduxEnhancer({
//   // Optionally pass options
// })

export const store = configureStore({
  reducer: {
    app: (store = {}) => store
  },
  middleware: [epicMiddleware],
  // enhancers: (getDefaultEnhancers) => {
  //   return getDefaultEnhancers.concat(sentryReduxEnhancer)
  // },
  devTools: process.env.NODE_ENV === 'development'
})
export type StoreType = ReturnType<typeof store.getState>

epicMiddleware.run(rootEpic)

export const useAppSelector = useSelector
export const useAppDispatch = (): typeof store.dispatch => {
  return useDispatch()
}

export const createBaseSelector = <S, N extends string>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  slice: Slice<S, any, N>
) => {
  return (store: unknown) => {
    const anyStore = store as Record<string, unknown>
    return anyStore[slice.name] as S
  }
}

export const useAction = <T, A extends Parameters<typeof store.dispatch>[0]>(
  factory: (p: T) => A
) => {
  const dispatch = useAppDispatch()

  return useCallback(
    (params: T) => {
      return dispatch(factory(params))
    },
    [dispatch, factory]
  )
}

export const useActionWithDeps = <
  T extends { deps: unknown },
  A extends Parameters<typeof store.dispatch>[0]
>(
  factory: (p: T) => A,
  deps: T['deps']
) => {
  const dispatch = useAppDispatch()
  return useCallback(
    (params: Omit<T, 'deps'>) => {
      return dispatch(factory({ deps, ...params } as T))
    },
    [dispatch, factory, deps]
  )
}

const slicesSet = new Set<Slice>()

export const registerSlice = (slices: Slice[]) => {
  slices.forEach((slice) => {
    slicesSet.add(slice)
  })

  store.replaceReducer(
    combineReducers({
      ...Array.from(slicesSet).reduce(
        (acc, slice) => {
          acc[slice.name] = slice.reducer
          return acc
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {} as Record<string, any>
      )
    })
  )
}

export const registerEpic = (epic: AppEpic) => {
  epic$.next(epic)
}
